import { motion } from 'framer-motion';
import { ArrowRight, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import heroImage from '../assets/hero/hero-1.png';

interface ProfessionalHeroProps {
  className?: string;
}

export const ProfessionalHero = ({ className = '' }: ProfessionalHeroProps) => {
  return (
    <div className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-theme-background ${className}`}>
      {/* Full-bleed visionary background */}
      <div className="absolute inset-0 w-full h-full scale-105">
        <img
          src={heroImage}
          alt="Laboratory professional reviewing health data on a screen"
          className="w-full h-full object-cover opacity-50"
          loading="eager"
        />
        {/* Deep cinematic overlay - Adjusted for better text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-theme-background/60 via-theme-background/80 to-theme-background" />
        
        {/* Animated ambient glow */}
        <motion.div 
            animate={{ 
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-theme-primary/20 blur-[150px] rounded-full"
        />
      </div>

      {/* Content container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="text-center space-y-12">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-theme-border bg-theme-surface/30 backdrop-blur-md mb-8">
                    <Globe className="w-4 h-4 text-theme-primary" />
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-theme-text-primary">The Future of Longevity</span>
                </div>

                <h1 className="text-6xl md:text-8xl font-black text-theme-text-primary leading-[0.9] tracking-tighter mb-8 max-w-5xl mx-auto drop-shadow-2xl">
                    OWN YOUR <br />
                    <span className="text-theme-primary italic filter drop-shadow-lg">
                        BIOLOGICAL FUTURE.
                    </span>
                </h1>

                <p className="text-xl md:text-2xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed font-light">
                    BIOTUNE is the first integrated <strong>Health Operating System</strong>. We provide the biological infrastructure to monitor, analyze, and optimize your life in real-time.
                </p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8"
            >
                <Link
                    to="/pricing"
                    className="group relative px-10 py-5 bg-theme-text-primary text-theme-text-inverse font-black uppercase tracking-widest rounded-full overflow-hidden transition-all hover:scale-105"
                >
                    <span className="relative z-10 flex items-center gap-3">
                        Join the Ecosystem
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </span>
                </Link>

                <Link
                    to="/features"
                    className="px-10 py-5 border border-theme-border text-theme-text-primary font-black uppercase tracking-widest rounded-full hover:bg-theme-surface/5 backdrop-blur-md transition-all"
                >
                    Explore the Pillars
                </Link>
            </motion.div>

            <div className="pt-24 grid grid-cols-2 md:grid-cols-4 gap-12 border-t border-theme-border/20 max-w-4xl mx-auto">
                <div>
                   <div className="text-2xl font-black text-theme-text-primary">$149+</div> 
                   <div className="text-[10px] text-theme-text-muted uppercase tracking-widest font-bold">Entry Point</div>
                </div>
                <div>
                   <div className="text-2xl font-black text-theme-text-primary">7 Pillars</div> 
                   <div className="text-[10px] text-theme-text-muted uppercase tracking-widest font-bold">Integrated OS</div>
                </div>
                <div>
                   <div className="text-2xl font-black text-theme-text-primary">Monthly</div> 
                   <div className="text-[10px] text-theme-text-muted uppercase tracking-widest font-bold">Bio-Loop</div>
                </div>
                <div>
                   <div className="text-2xl font-black text-theme-text-primary">24/7</div> 
                   <div className="text-[10px] text-theme-text-muted uppercase tracking-widest font-bold">Concierge Care</div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};